import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import '../../../css/accounts/reports/LedgerPaymentReport.css';

const LedgerPaymentReport = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination States (15 per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Fetch Ledger Payments from Firestore 'ledgerPayments' Collection
  useEffect(() => {
    const q = query(collection(db, 'ledgerPayments'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          date: data.date || '',
          voucherNo: data.voucherNo || 'N/A',
          expCode: data.expCode || '',
          expName: data.expName || '',
          description: data.description || '',
          amount: Number(data.amount) || 0,
          payMode: data.payMode || 'By Cash',
        };
      });

      setPayments(records);
      setFilteredPayments(records);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching ledger payment report: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter Logic
  useEffect(() => {
    let result = payments;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.voucherNo.toLowerCase().includes(term) ||
          item.expCode.toLowerCase().includes(term) ||
          item.expName.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term)
      );
    }

    if (startDate) {
      result = result.filter((item) => item.date >= startDate);
    }

    if (endDate) {
      result = result.filter((item) => item.date <= endDate);
    }

    setFilteredPayments(result);
    setCurrentPage(1); // Reset pagination on filter
  }, [searchTerm, startDate, endDate, payments]);

  // Aggregate Calculations
  const totalVoucherCount = filteredPayments.length;
  const totalPaidAmount = filteredPayments.reduce((sum, item) => sum + item.amount, 0);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="ledger-report-container">
      <div className="ledger-report-card">
        
        {/* Header */}
        <div className="report-header">
          <div>
            <h2 className="report-title">Ledger Payment Report</h2>
            <p className="report-subtitle">Historical breakdown of all posted ledger expense vouchers</p>
          </div>
          <button className="btn-export-csv" onClick={() => window.print()}>
            🖨️ Print / Export
          </button>
        </div>

        {/* Top Summary KPI Cards */}
        <div className="report-summary-grid">
          <div className="summary-card total-vouchers-card">
            <div className="card-info">
              <span className="card-label">Total Payment Vouchers</span>
              <span className="card-value">{totalVoucherCount}</span>
            </div>
            <div className="card-icon">📑</div>
          </div>

          <div className="summary-card total-paid-card">
            <div className="card-info">
              <span className="card-label">Total Amount Paid</span>
              <span className="card-value">AED {totalPaidAmount.toFixed(2)}</span>
            </div>
            <div className="card-icon">💸</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="report-filter-bar">
          <div className="filter-group main-search">
            <input
              type="text"
              placeholder="Search Voucher No, Code, Ledger Name, Remarks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="report-input"
            />
          </div>

          <div className="filter-group date-filters">
            <div className="date-field">
              <label>From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="report-input date-input"
              />
            </div>
            <div className="date-field">
              <label>To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="report-input date-input"
              />
            </div>
          </div>
        </div>

        {/* Ledger Payments Table */}
        <div className="report-table-wrapper">
          <table className="report-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>S.No</th>
                <th>Date</th>
                <th>Voucher Number</th>
                <th>Particulars (Code & Ledger Name)</th>
                <th>Pay Mode</th>
                <th style={{ textAlign: 'right' }}>Amount (AED)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="status-cell">Loading ledger payment report...</td>
                </tr>
              ) : currentDisplayedPayments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="status-cell">No ledger payment records found.</td>
                </tr>
              ) : (
                currentDisplayedPayments.map((item, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;
                  return (
                    <tr key={item.id}>
                      <td><strong>{serialNumber}</strong></td>
                      <td>{item.date || 'N/A'}</td>
                      <td className="voucher-code-text">{item.voucherNo}</td>
                      <td className="particulars-text">
                        <strong>{item.expCode}</strong> - {item.expName}
                        {item.description && <span className="sub-desc"> ({item.description})</span>}
                      </td>
                      <td>
                        <span className={`mode-pill ${item.payMode === 'By Cash' ? 'cash' : 'bank'}`}>
                          {item.payMode}
                        </span>
                      </td>
                      <td className="currency-cell" style={{ textAlign: 'right' }}>
                        AED {item.amount.toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {!loading && filteredPayments.length > 0 && (
          <div className="report-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredPayments.length)}</strong> of <strong>{filteredPayments.length}</strong> vouchers
            </div>

            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prev && page - prev > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default LedgerPaymentReport;